import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { createSelector } from '@reduxjs/toolkit';
import { twMerge } from 'tailwind-merge';
import { Card } from '@/services/table/tableTypes';
import { SeatDto, TableState, WinningData } from '@/services/table/tableState';
import { BetCollection, LastOperationLabel } from '@/services/table/tableOperations';
import { ReducerState } from '@/services/common/lib/reducerState';
import { usePositionDispatch, usePositionStore } from '../../context/PositionContext';
import { setUserChipRef } from '../../context/positionActions';
import useIsMobileView from '../../../../hooks/useIsMobileView';
import useGetUserProfile from '@/services/user/queries/useGetUserProfile';
import styles from './UserPanel.module.css';
import calculatAnimationTargetPosition from '../../utils/calculatAnimationTargetPosition';
import GuestCards from '../GuestCards/GuestCards';
import AnimatedPokerChipStacks from '../PokerChips/AnimatedPokerChips';
import DealerIcon from './DealerIcon';
import Avatar from '@/features/avatar/Avatar';
import PlayerNotesModal from '@/features/player-notes/PlayerNotesModal';
import PlayerCards from '../PlayerCards/PlayerCards';
import UserPanelBadge, { UserBadgeVariants } from '../UserBadge/UserPanelBadge';
import UserPanelBox from '../UserPanelBox/UserPanelBox';
import UserPanelTimer from '../UserPanelTimer/UserPanelTimer';
import Pulse from '../visual-effects/Pulse/Pulse';
import { ReactComponent as PhenomBadge } from '@/assets/phenom-badge.svg';
import TooltipWrapper, { TriggerType } from '@/components/new-design/ToolTipWrapper';

// Constants
const STACK_ENTROPY = 0;

// Enums
export enum BetPosition {
  Left = 'left',
  Right = 'right',
  Top = 'top',
  Bottom = 'bottom',
  BottomRight = 'bottom-right',
  BottomLeft = 'bottom-left',
  TopRight = 'top-right',
  TopLeft = 'top-left',
}

// Types
interface UserPanelProps {
  userId: string;
  self: boolean;
  seat: SeatDto | null;
  alias: string;
  tableId: string;
  stack?: number;
  isDealer?: boolean;
  playerBet?: number;
  holeCards?: Card[];
  lastOperationLabel?: LastOperationLabel;
  betPosition?: BetPosition;
  className?: string;
  bigBlind: number;
  winningData?: WinningData;
  isDisconnected: boolean;
  tableState: TableState;
}

// Selectors
const selectTableActionPerformed = (state: ReducerState, tableId: string) => state.tables?.[tableId]?.actionPerformed;

const makeSelectShouldAnimate = (seat: SeatDto | null) =>
  createSelector([selectTableActionPerformed], (actionPerformed) => {
    if (seat?.playerIndex === undefined || seat?.playerIndex === null) return false;
    const betCollectionAction = actionPerformed as BetCollection;
    const isBetCollection = betCollectionAction?.subtype === 'BetCollection';
    const isCollectionSeatBet = !!betCollectionAction?.bets?.[seat.playerIndex];
    return isBetCollection && isCollectionSeatBet;
  });

const UserPanel: FC<UserPanelProps> = ({
  userId,
  alias,
  self,
  seat,
  stack,
  isDealer,
  playerBet,
  holeCards,
  lastOperationLabel,
  winningData,
  tableId,
  betPosition = BetPosition.TopLeft,
  className,
  bigBlind,
  isDisconnected,
  tableState,
}) => {
  const user = useSelector((state: ReducerState) => state.auth);
  const selectShouldAnimate = useMemo(() => makeSelectShouldAnimate(seat), [seat]);
  const shouldAnimate = useSelector((state: ReducerState) => selectShouldAnimate(state, tableId));
  const joining = useSelector((state: ReducerState) => state.tables?.[tableId]?.joining);
  const displayInBBEnabled = useSelector((state: ReducerState) => state.tables?.[tableId]?.displayInBBEnabled);
  const shouldDisplayInBB = (!!bigBlind && displayInBBEnabled) ?? user.settings?.stackInBigBlindsEnabled;
  const fannedCardsEnabled = useSelector(
    (state: ReducerState) => state.auth.settings?.fannedCardsDisplayEnabled || true,
  );

  const isMobileView = useIsMobileView({ checkForUserAgent: true });
  const [showNotesModal, setShowNotesModal] = useState(false);
  const chipRef = useRef<HTMLDivElement>(null);
  const {
    state: { potRef, tableRef },
  } = usePositionStore();
  const dispatch = usePositionDispatch();

  useEffect(() => {
    if (chipRef?.current && tableRef?.current && seat?.seat !== undefined) {
      dispatch(setUserChipRef(seat?.seat || 0, chipRef));
    }
  }, [dispatch, seat?.seat, tableRef, chipRef]);

  const toPosition = useMemo(
    () => calculatAnimationTargetPosition(tableRef, chipRef, potRef),
    [tableRef, chipRef, potRef],
  );

  const chipsPosition = useMemo(() => {
    switch (betPosition) {
      case BetPosition.TopLeft:
        return styles.chipsTL;
      case BetPosition.TopRight:
        return styles.chipsTR;
      case BetPosition.BottomLeft:
        return styles.chipsBL;
      case BetPosition.BottomRight:
        return styles.chipsBR;
      case BetPosition.Left:
        return styles.chipsL;
      case BetPosition.Right:
        return styles.chipsR;
      case BetPosition.Top:
        return styles.chipsT;
      case BetPosition.Bottom:
        return styles.chipsB;
      default:
        return '';
    }
  }, [betPosition]);

  const chipsFlexDirection = useMemo(() => {
    if (betPosition.includes(BetPosition.Left) || betPosition.includes(BetPosition.Right)) {
      return 'col';
    }
    return 'row';
  }, [betPosition]);

  const { data: profileData } = useGetUserProfile(
    useMemo(
      () => ({
        userId,
        enabled: !self,
      }),
      [userId, self],
    ),
  );
  const shouldShowAmbassadorBadge = useMemo(() => {
    if (self) return user.isAmbassador;
    else return profileData?.data.isAmbassador;
  }, [self, user.isAmbassador, profileData?.data.isAmbassador]);
  if (!seat) {
    return null;
  }

  const isPlayerWinner = seat.playerIndex != null && winningData?.playerIndexes.includes(seat.playerIndex);
  const notesColor = !self && profileData?.data.notesColor ? profileData.data.notesColor : '';

  return (
    <div className="relative z-50">
      <div className={twMerge('relative', 'gap-1', 'min-h-34', styles.panelContainer, className)}>
        <div className={twMerge(chipsPosition, 'relative z-20')}>
          <div
            className={twMerge(styles.chips, isMobileView ? 'text-[10px]' : '')}
            ref={chipRef}
            style={{
              direction: betPosition.includes(BetPosition.Left) ? 'rtl' : 'ltr',
            }}
          >
            {isDealer && <DealerIcon />}
            <AnimatedPokerChipStacks
              shouldAnimate={shouldAnimate}
              bigBlind={bigBlind}
              amount={playerBet}
              entropy={STACK_ENTROPY}
              flexDirection={chipsFlexDirection}
              betPosition={betPosition}
              tableId={tableId}
              fromPosition={{ x: 0, y: 0 }}
              toPosition={toPosition}
              useTrigger
              skipAnimation
            />
          </div>
        </div>

        <div
          className={twMerge('flex flex-col items-center justify-end gap-1', styles.avatar, !self && 'cursor-pointer')}
          onClick={() => {
            if (!self) setShowNotesModal(true);
          }}
        >
          <div className="relative flex">
            <div className={twMerge('flex flex-col items-center justify-center')}>
              {isPlayerWinner && (
                <div className="absolute bottom-0 right-0 z-0 flex w-25 items-center justify-center">
                  {/* <Fire /> */}
                </div>
              )}
              {seat?.isActingPlayer && (
                <div className="absolute z-0 flex w-25 items-center justify-center">
                  <Pulse />
                </div>
              )}
              <Avatar
                className={`h-34 w-34 ${!seat.active ? 'opacity-50' : ''}`}
                userId={userId}
                border={false}
                round={false}
              />

              {!self && (
                <GuestCards
                  isMobileView={isMobileView}
                  winningData={winningData}
                  holeCards={holeCards}
                  cardAreFanned={fannedCardsEnabled}
                />
              )}
              <div
                className={twMerge('z-10 flex flex-col items-center justify-start', isMobileView ? 'text-[9px]' : '')}
              >
                <div className="relative">
                  {shouldShowAmbassadorBadge && (
                    <div className="absolute left-0 top-0 -translate-x-1/3 -translate-y-1/3">
                      <TooltipWrapper
                        content={
                          <span>
                            Phenom <span className="text-ph-purple">Team Pro</span>
                          </span>
                        }
                        fullDimensions={false}
                        triggerType={TriggerType.CLICK}
                      >
                        <PhenomBadge className="h-7 w-7" />
                      </TooltipWrapper>
                    </div>
                  )}
                  <UserPanelBox
                    borderColor={notesColor}
                    isPlayersTurn={seat.isActingPlayer}
                    alias={alias}
                    stack={stack}
                    discardedCount={seat.cardsDiscarded}
                    isActive={seat.active}
                    label={lastOperationLabel?.label}
                    status={seat.status}
                    isDisconnected={isDisconnected}
                    isCappedGame={tableState?.currentVariant?.capped}
                    joining={!!self && joining}
                    shouldDisplayInBB={!!shouldDisplayInBB}
                    bigBlind={bigBlind}
                  />
                  {!(seat?.holeCards?.length === 0) && (
                    <div
                      className={twMerge(
                        'absolute -right-4 bottom-4',
                        (seat.skippedBB || seat.skippedSB) && 'bottom-0',
                      )}
                    >
                      <UserPanelBadge content={seat.cardsDiscarded?.toString()} color={UserBadgeVariants.blue} />
                    </div>
                  )}
                  {seat.skippedBB && (
                    <div className="absolute -right-4 bottom-4">
                      <UserPanelBadge content="BB" color={UserBadgeVariants.orange} />
                    </div>
                  )}
                  {seat.skippedSB && (
                    <div className="absolute -right-4 bottom-4">
                      <UserPanelBadge content="SB" color={UserBadgeVariants.green} />
                    </div>
                  )}
                </div>
                <div className={twMerge('mt-[0.125em] h-1 w-full rounded-full pb-2')}>
                  <UserPanelTimer
                    usingTimeBank={seat.usingTimeBank}
                    timer={seat.timer}
                    timeRemaining={seat.timeRemaining}
                  />
                </div>
              </div>
            </div>
            {self && (
              <div className="z-1 absolute left-1/2 top-0 -translate-x-1/2 translate-y-[3em]">
                <PlayerCards tableId={tableId} tableState={tableState} cardAreFanned={fannedCardsEnabled} />
              </div>
            )}
          </div>
        </div>
      </div>
      {showNotesModal && (
        <PlayerNotesModal
          userId={userId}
          open={showNotesModal}
          setClose={() => setShowNotesModal(false)}
          tableStack={stack}
        />
      )}
    </div>
  );
};

export default UserPanel;
